/**
 * Cold-start deployment probing (DEC-789 S2) — pulled out of client.ts (module-health ratchet,
 * STD-2/DEC-1504: client.ts is grandfathered shrink-only, so a new fix recovers the room it
 * needs by splitting out a genuine responsibility rather than widening the baseline).
 *
 * "Parse the fallback URL list, then probe each candidate's /api/key-check until one answers
 * ok:true" is self-contained — it takes a candidate list and an API key and returns the first
 * live deployment (or null), with no dependency on client.ts's own state. client.ts's
 * `resolveDeploymentUrl` still owns WHEN to probe (warm-path short-circuit, no-fallback
 * short-circuit, caching the winner on `state().deploymentUrl`) — this module owns HOW.
 */

/** DEC-789 S2: parse comma-separated fallback deployment URLs from env. */
export function parseFallbackUrls(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(",").map((u) => u.trim()).filter(Boolean);
}

/**
 * Probe each candidate in order; return the first that answers ok:true to `/api/key-check`, or
 * null if every candidate failed.
 *
 * WP-575: the 3s timeout here is DELIBERATELY not route-derived, and is not the class of
 * timeout that work package's budget replaced. `/api/key-check` is a different endpoint from
 * the `/api/aki` gateway — it is not a registered route, has no entry in the route contract, and
 * no budget to derive from. It is also a REACHABILITY PROBE whose whole job is to pick the live
 * deployment out of several candidates by failing fast: a longer wait here would multiply across
 * candidates and delay every call, and a probe giving up costs nothing (the caller falls through
 * to the primary URL and the real call reports the real error). Do not "unify" this with the
 * gateway budget.
 */
export async function probeDeploymentCandidates(candidates: string[], apiKey: string): Promise<string | null> {
  for (const candidate of candidates) {
    try {
      const probeRes = await fetch(`${candidate}/api/key-check`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${apiKey}`, "Content-Type": "application/json" },
        signal: AbortSignal.timeout(3000),
      });
      if (probeRes.ok) {
        const data = (await probeRes.json()) as { ok: boolean };
        if (data.ok) return candidate;
      }
    } catch {
      // Probe failed (timeout, network error, etc.) — try next candidate.
    }
  }
  return null;
}
